import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Build timestamp injected by vite.config.ts → `define`. Shows up in
      // the fallback UI so a user reporting a blank page can copy this
      // string and we can instantly confirm whether they're seeing the
      // newly deployed bundle or still hitting a stale Cloudflare Pages
      // cache.
      const buildStamp =
        typeof __BUILD_TIMESTAMP__ === 'string' && __BUILD_TIMESTAMP__
          ? __BUILD_TIMESTAMP__
          : 'unknown';

      return (
        <div
          // position: fixed + inset: 0 escapes any stacking context the
          // body ::before / ::after (z-index 9998/9999 with
          // mix-blend-mode: screen + opacity 0.85) might create, so the
          // fallback always paints on top.
          role="alert"
          aria-live="assertive"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            backgroundColor: 'rgba(10, 6, 22, 0.94)',
            color: '#fff8e7',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '14px',
            lineHeight: 1.5,
            overflow: 'auto',
            padding: '24px',
          }}
        >
          <div
            style={{
              padding: '24px',
              color: '#fff8e7',
              backgroundColor: 'rgba(28, 16, 48, 0.96)',
              border: '2px solid #ff71ce',
              borderRadius: '10px',
              boxShadow: '0 0 24px rgba(255, 113, 206, 0.55)',
              maxWidth: '900px',
              overflowX: 'auto',
            }}
          >
          <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', color: '#01cdfe' }}>
            Psydechat hit a render error
          </h2>
          <p style={{ margin: '0 0 8px 0' }}>
            The app crashed before it could paint. The error message and stack trace are below — please
            share them when reporting this issue.
          </p>
          <p style={{ margin: '0 0 12px 0', opacity: 0.7, fontSize: '12px' }}>
            Build: <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 6px', borderRadius: '4px' }}>{buildStamp}</code>
          </p>
          <details open style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            {this.state.errorInfo && (
              <>
                <br />
                {this.state.errorInfo.componentStack}
              </>
            )}
          </details>
          <p style={{ marginTop: '12px', opacity: 0.7, fontSize: '12px' }}>
            Try a hard refresh (Ctrl/Cmd+Shift+R). If the issue persists, paste the error above.
          </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
